import { useEffect, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import "./CoTrader.css";

// Port of GlobalLink pre-trade's analysis-steps collapsible (CollapsibleDiv +
// StreamingDots). While INFO steps stream in, the header shows the live step
// with a shimmer and an elapsed timer; once done it settles on
// "Analysis steps · N" and freezes the timer on the total duration.

export const StreamingDots = () => (
  <span className="ss-dots" aria-hidden="true">
    <span className="ss-dot" />
    <span className="ss-dot" />
  </span>
);

// The visible text of a step, used for the live header label.
const nodeText = (node) => {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join("");
  if (node?.props?.children !== undefined) return nodeText(node.props.children);
  return "";
};

const formatElapsed = (seconds) =>
  seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;

const AnalysisSteps = ({ steps, active = false, footer }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [elapsed, setElapsed] = useState(null);
  const startedAtRef = useRef(null);

  // Ticks while steps stream in, then freezes on the final duration so the
  // header still reports how long the analysis took.
  useEffect(() => {
    if (!active) return undefined;
    if (startedAtRef.current == null) {
      startedAtRef.current = Date.now();
      setElapsed(0);
    }
    const started = startedAtRef.current;
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - started) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [active]);

  const rows = steps || [];
  const liveLabel = active
    ? nodeText(rows[rows.length - 1]).replace(/\s+/g, " ").trim()
    : "";
  const label = liveLabel
    ? liveLabel
    : rows.length > 1
      ? `Analysis steps · ${rows.length}`
      : "Analysis steps";

  return (
    <div className="as-root">
      <button
        type="button"
        className="as-toggle"
        data-active={active ? "true" : "false"}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        {active && <StreamingDots />}
        <span
          className={active ? "as-label ss-shimmer" : "as-label"}
          aria-live={active ? "polite" : undefined}
        >
          {label}
        </span>
        {elapsed != null && <span className="as-elapsed">{formatElapsed(elapsed)}</span>}
        <ChevronRight
          className="as-chevron"
          data-open={isOpen ? "true" : "false"}
          size={14}
          aria-hidden="true"
        />
      </button>

      <div className="as-panel" data-open={isOpen ? "true" : "false"}>
        <div className="as-panel-inner">
          <div className="as-list">
            {rows.map((row, index) => (
              <div className="as-step" key={index}>
                {row}
              </div>
            ))}
            {footer ? <div className="as-footer">{footer}</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalysisSteps;
